import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 语言学习动画 - 单词记忆闪卡
 * 
 * 核心效果：
 * 1. 单词卡片翻转动画
 * 2. 发音符标注动画
 * 3. 例句逐字出现
 * 4. 记忆曲线提示
 */

interface WordCard {
  word: string;
  phonetic: string;
  meaning: string;
  example: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export const exampleWordCards: WordCard[] = [
  {
    word: 'Ephemeral',
    phonetic: '/ɪˈfem(ə)rəl/',
    meaning: '短暂的，瞬息的',
    example: 'Fame is ephemeral in the entertainment industry.',
    difficulty: 'hard',
  },
  {
    word: 'Serendipity',
    phonetic: '/ˌserənˈdɪpəti/',
    meaning: '意外发现的惊喜',
    example: 'Meeting her at the party was pure serendipity.',
    difficulty: 'hard',
  },
];

export const LanguageLearningAnimation: React.FC<{
  words?: WordCard[];
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
}> = ({
  words = exampleWordCards,
  primaryColor = '#FF6B35',
  secondaryColor = '#4ECDC4',
  backgroundColor = '#0A0E27',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const framesPerWord = 300;
  const currentWordIndex = Math.min(Math.floor(frame / framesPerWord), words.length - 1);
  const currentWord = words[currentWordIndex];
  const localFrame = frame - currentWordIndex * framesPerWord;

  // 卡片翻转动画（快速：0-20帧）
  const flipProgress = interpolate(
    localFrame,
    [0, 20],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );
  
  const showFront = flipProgress < 0.5;
  const rotationY = interpolate(
    flipProgress,
    [0, 0.5, 1],
    [0, 90, 180]
  );

  // 单词出现动画（快速：15-25帧）
  const wordOpacity = interpolate(
    localFrame,
    [15, 25],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );
  
  const wordScale = spring({
    frame: localFrame - 15,
    from: 0.8,
    to: 1,
    fps,
    config: { damping: 12, stiffness: 200 },
  });

  // 发音符动画（快速：25-35帧）
  const phoneticOpacity = interpolate(
    localFrame,
    [25, 35],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );

  // 意义出现动画（快速：35-50帧）
  const meaningOpacity = interpolate(
    localFrame,
    [35, 50],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );
  
  const meaningY = interpolate(
    localFrame,
    [35, 50],
    [20, 0],
    { extrapolateRight: 'clamp' }
  );

  // 例句动画（慢速：70-240帧，约5.7秒）
  const exampleChars = currentWord.example.split('');
  const charsVisible = Math.floor(
    interpolate(
      localFrame,
      [70, 240],
      [0, exampleChars.length],
      { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
    )
  );
  const visibleExample = exampleChars.slice(0, charsVisible).join('');

  // 难度标签
  const difficultyColors = {
    easy: '#10B981',
    medium: '#F59E0B',
    hard: '#EF4444',
  };

  // 进度条
  const progressBarWidth = interpolate(
    localFrame,
    [0, framesPerWord],
    [0, 100],
    { extrapolateRight: 'clamp' }
  );

  // 记忆提示动画（260-280帧）
  const memoryTipOpacity = interpolate(
    localFrame,
    [260, 280],
    [0, 1],
    { extrapolateRight: 'clamp', extrapolateLeft: 'clamp' }
  );

  return (
    <AbsoluteFill
      style={{
        background: `linear-gradient(135deg, ${backgroundColor} 0%, #1a1f3a 100%)`,
        fontFamily: 'Arial, sans-serif',
        color: 'white',
        padding: 60,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* 顶部进度指示器 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 60,
          right: 60,
          height: 8,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${progressBarWidth}%`,
            height: '100%',
            background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            borderRadius: 4,
          }}
        />
      </div>

      {/* 难度标签 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          right: 60,
          padding: '8px 20px',
          background: difficultyColors[currentWord.difficulty],
          borderRadius: 20,
          fontSize: 24,
          fontWeight: 'bold',
          textTransform: 'uppercase',
        }}
      >
        {currentWord.difficulty}
      </div>

      {/* 主卡片 */}
      <div
        style={{
          width: 900,
          height: 420,
          perspective: '1000px',
          opacity: wordOpacity,
          transform: `scale(${wordScale})`,
        }}
      >
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'relative',
            transformStyle: 'preserve-3d',
            transform: `rotateY(${showFront ? 0 : 180}deg)`,
            transition: 'transform 0.8s',
          }}
        >
          {/* 正面 - 单词 */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              background: `linear-gradient(135deg, ${primaryColor}22 0%, ${secondaryColor}22 100%)`,
              border: `4px solid ${primaryColor}`,
              borderRadius: 30,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 40,
            }}
          >
            <div
              style={{
                fontSize: 100,
                fontWeight: 'bold',
                marginBottom: 20,
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {currentWord.word}
            </div>
            
            <div
              style={{
                fontSize: 48,
                color: secondaryColor,
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                opacity: phoneticOpacity,
              }}
            >
              {currentWord.phonetic}
            </div>
          </div>

          {/* 背面 - 意义 */}
          <div
            style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)',
              background: `linear-gradient(135deg, ${secondaryColor}22 0%, ${primaryColor}22 100%)`,
              border: `4px solid ${secondaryColor}`,
              borderRadius: 30,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 60,
            }}
          >
            <div
              style={{
                fontSize: 56,
                fontWeight: 'bold',
                marginBottom: 40,
                textAlign: 'center',
                opacity: meaningOpacity,
                transform: `translateY(${meaningY}px)`,
              }}
            >
              {currentWord.meaning}
            </div>
          </div>
        </div>
      </div>

      {/* 例句 */}
      <div
        style={{
          position: 'absolute',
          bottom: 150,
          left: 80,
          right: 80,
          fontSize: 36,
          lineHeight: 1.6,
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '30px 40px',
          borderRadius: 20,
          border: '2px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <span style={{ opacity: 0.5 }}>例句: </span>
        {visibleExample}
        {charsVisible < exampleChars.length && (
          <span style={{ opacity: 0.3 }}>|</span>
        )}
      </div>

      {/* 记忆提示 */}
      <div
        style={{
          position: 'absolute',
          bottom: 80,
          left: 80,
          right: 80,
          fontSize: 28,
          textAlign: 'center',
          opacity: memoryTipOpacity,
          color: secondaryColor,
        }}
      >
        💡 记忆提示：将单词与具体场景关联，记忆效果提升 3 倍
      </div>

      {/* 单词计数器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          right: 60,
          fontSize: 32,
          opacity: 0.7,
        }}
      >
        {currentWordIndex + 1} / {words.length}
      </div>
    </AbsoluteFill>
  );
};
